// =============================================
// SERVICE: Dados Mestres para Modo Offline
// Sistema sempre trabalha em modo offline-first.
// Baixa e armazena em IndexedDB os dados necessarios
// para o conferente trabalhar 100% offline.
// Sincroniza automaticamente quando online.
// =============================================

import { getConferenteToken } from './conferente';
import { handleConferenteSessionExpired } from './conferenteAuth';
import { buildApiUrl } from './config';

const DB_NAME = 'confereai_dados_offline';
const DB_VERSION = 2;
const STORE_NAME = 'dados_mestres';
const META_KEY = 'offline_mode_meta';
const SYNC_INTERVAL_MONTAGENS = 10 * 60 * 1000; // 10 minutos - montagens de cargas
const SYNC_INTERVAL_CONFERENCIAS = 5 * 60 * 1000; // 5 minutos - conferencias

// =============================================
// TYPES
// =============================================

export interface OfflineMeta {
  preparado_em: string;
  clientes_count: number;
  filiais_count: number;
  areas_count: number;
  operacoes_count: number;
  conferencias_count: number;
  montagens_count: number;
}

export interface OfflineStats {
  pronto: boolean;
  meta: OfflineMeta | null;
}

export interface ProgressCallback {
  (step: string, current: number, total: number): void;
}

// =============================================
// IndexedDB
// =============================================

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'tipo' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function salvarDados(tipo: string, data: any[]): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.put({ tipo, data, cached_at: new Date().toISOString() });
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error || new Error('Transacao abortada')); };
  });
}

async function carregarDados<T = any>(tipo: string): Promise<T[] | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(tipo);
    req.onsuccess = () => {
      const result = req.result;
      db.close();
      resolve(result ? result.data : null);
    };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

async function salvarMeta(meta: OfflineMeta): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.put({ tipo: META_KEY, data: meta, cached_at: new Date().toISOString() });
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error || new Error('Transacao abortada')); };
  });
}

async function carregarMeta(): Promise<OfflineMeta | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(META_KEY);
    req.onsuccess = () => {
      const result = req.result;
      db.close();
      resolve(result ? result.data : null);
    };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

// =============================================
// FETCH com token do conferente
// =============================================

async function fetchConferente<T>(url: string): Promise<T> {
  const token = getConferenteToken();
  if (!token) throw new Error('Conferente nao autenticado');

  const res = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (res.status === 401) {
    handleConferenteSessionExpired();
    return new Promise(() => {});
  }

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || `Erro ao buscar dados (${res.status})`);
  }

  return res.json();
}

// =============================================
// SINCRONIZACAO AUTOMATICA DE DADOS MESTRES
// Baixa todos os dados necessarios para o conferente
// trabalhar offline. Chamado automaticamente quando online.
// =============================================

const TOTAL_STEPS = 6;
let syncEmAndamento = false;
let ultimoSyncMontagens: number = 0;
let ultimoSyncConferencias: number = 0;

// =============================================
// SYNC PARCIAL: Configuracoes (clientes, filiais, areas)
// Chamado no login para garantir cache imediato.
// =============================================

export async function sincronizarConfiguracoes(onProgress?: ProgressCallback, _interno?: boolean): Promise<{ clientes: any[]; filiais: any[]; areas: any[] }> {
  if (!navigator.onLine) throw new Error('Sem conexao com a internet');
  if (!getConferenteToken()) throw new Error('Conferente nao autenticado');
  if (!_interno && syncEmAndamento) throw new Error('Sincronizacao ja em andamento');

  let step = 0;
  const totalSteps = 3;
  const progress = (label: string) => {
    step++;
    onProgress?.(label, step, totalSteps);
  };

  // 1. Clientes com etiqueta configs
  progress('Sincronizando clientes...');
  let clientes: any[] = [];
  try {
    const clientesResult = await fetchConferente<{ success: boolean; clientes: any[] }>(
      buildApiUrl('/clientes?limit=500&ativo=true')
    );
    if (clientesResult.success) {
      clientes = clientesResult.clientes;
    }
  } catch {
    clientes = [];
  }
  await salvarDados('clientes', clientes);

  // 2. Filiais
  progress('Sincronizando filiais...');
  let filiais: any[] = [];
  try {
    const filiaisResult = await fetchConferente<{ success: boolean; filiais: any[] }>(
      buildApiUrl('/montar-carga/filiais?ativo=true')
    );
    if (filiaisResult.success) {
      filiais = filiaisResult.filiais;
    }
  } catch {
    filiais = [];
  }
  await salvarDados('filiais', filiais);

  // 3. Areas de entrega
  progress('Sincronizando areas de entrega...');
  let areas: any[] = [];
  try {
    const areasResult = await fetchConferente<{ success: boolean; areas: any[] }>(
      buildApiUrl('/montar-carga/areas?ativo=true')
    );
    if (areasResult.success) {
      areas = areasResult.areas;
    }
  } catch {
    areas = [];
  }
  await salvarDados('areas', areas);

  if (!_interno) await atualizarMetaParcial();
  return { clientes, filiais, areas };
}

// =============================================
// SYNC PARCIAL: Montagens de cargas (operacoes + paletes)
// Chamado a cada 10 minutos para manter dados atualizados.
// =============================================

export async function sincronizarMontagensCargas(onProgress?: ProgressCallback, _interno?: boolean): Promise<{ operacoes: any[]; montagens: any[] }> {
  if (!navigator.onLine) throw new Error('Sem conexao com a internet');
  if (!getConferenteToken()) throw new Error('Conferente nao autenticado');
  if (!_interno && syncEmAndamento) throw new Error('Sincronizacao ja em andamento');

  let step = 0;
  const totalSteps = 2;
  const progress = (label: string) => {
    step++;
    onProgress?.(label, step, totalSteps);
  };

  // 1. Operacoes ativas com paletes
  progress('Sincronizando montagens de cargas...');
  let operacoes: any[] = [];
  try {
    const opsResult = await fetchConferente<{ success: boolean; operacoes: any[] }>(
      buildApiUrl('/montar-carga/operacoes?limit=100')
    );
    if (opsResult.success && opsResult.operacoes) {
      for (const op of opsResult.operacoes) {
        try {
          const palResult = await fetchConferente<{ success: boolean; paletes: any[] }>(
            buildApiUrl(`/montar-carga/operacoes/${op.id}/paletes`)
          );
          if (palResult.success) {
            op.paletes = palResult.paletes;
          }
        } catch {
          op.paletes = [];
        }
      }
      operacoes = opsResult.operacoes;
    }
  } catch {
    operacoes = [];
  }
  await salvarDados('operacoes', operacoes);

  // 2. Montagens complementares (em andamento)
  progress('Sincronizando dados complementares...');
  let montagens: any[] = [];
  try {
    const montagensResult = await fetchConferente<{ success: boolean; operacoes: any[] }>(
      buildApiUrl('/montar-carga/operacoes?status=em_conferencia,em_carregamento,em_transito&limit=200')
    );
    if (montagensResult.success) {
      montagens = montagensResult.operacoes;
    }
  } catch {
    montagens = [];
  }
  await salvarDados('montagens', montagens);

  ultimoSyncMontagens = Date.now();
  if (!_interno) await atualizarMetaParcial();
  return { operacoes, montagens };
}

// =============================================
// SYNC PARCIAL: Conferencias ativas
// Chamado imediatamente ao reconectar (ficar online).
// =============================================

export async function sincronizarConferencias(onProgress?: ProgressCallback, _interno?: boolean): Promise<{ conferencias: any[] }> {
  if (!navigator.onLine) throw new Error('Sem conexao com a internet');
  if (!getConferenteToken()) throw new Error('Conferente nao autenticado');
  if (!_interno && syncEmAndamento) throw new Error('Sincronizacao ja em andamento');

  const progress = (label: string) => {
    onProgress?.(label, 1, 1);
  };

  progress('Sincronizando conferencias ativas...');
  let conferencias: any[] = [];
  try {
    const confResult = await fetchConferente<{ success: boolean; conferencias: any[] }>(
      buildApiUrl('/conferente/conferencias?status=em_andamento&limit=50')
    );
    if (confResult.success) {
      for (const conf of confResult.conferencias) {
        try {
          const detResult = await fetchConferente<{ success: boolean; conferencia: any; itens: any[]; resumo_leituras: any }>(
            buildApiUrl(`/conferencias/${conf.id}`)
          );
          if (detResult.success) {
            conf.itens = detResult.itens;
            conf.resumo_leituras = detResult.resumo_leituras;
          }
        } catch {
          // Continuar sem detalhes
        }
      }
      conferencias = confResult.conferencias;
    }
  } catch {
    conferencias = [];
  }
  await salvarDados('conferencias', conferencias);

  ultimoSyncConferencias = Date.now();
  if (!_interno) await atualizarMetaParcial();
  return { conferencias };
}

// =============================================
// SYNC COMPLETA: Todos os dados mestres
// Orquestra as 3 syncs parciais.
// =============================================

export async function sincronizarDadosMestres(onProgress?: ProgressCallback): Promise<OfflineMeta> {
  if (!navigator.onLine) {
    throw new Error('Sem conexao com a internet');
  }

  const token = getConferenteToken();
  if (!token) {
    throw new Error('Conferente nao autenticado');
  }

  if (syncEmAndamento) {
    throw new Error('Sincronizacao ja em andamento');
  }

  syncEmAndamento = true;

  let step = 0;
  const progress = (label: string) => {
    step++;
    onProgress?.(label, step, TOTAL_STEPS);
  };

  try {
    // 1-3. Configuracoes (clientes, filiais, areas)
    const { clientes, filiais, areas } = await sincronizarConfiguracoes((label) => progress(label), true);

    // 4-5. Montagens de cargas (operacoes + complementares)
    const { operacoes, montagens } = await sincronizarMontagensCargas((label) => progress(label), true);

    // 6. Conferencias ativas
    const { conferencias } = await sincronizarConferencias((label) => progress(label), true);

    // Salvar metadata
    const meta: OfflineMeta = {
      preparado_em: new Date().toISOString(),
      clientes_count: clientes.length,
      filiais_count: filiais.length,
      areas_count: areas.length,
      operacoes_count: operacoes.length,
      conferencias_count: conferencias.length,
      montagens_count: montagens.length,
    };
    await salvarMeta(meta);

    return meta;
  } finally {
    syncEmAndamento = false;
  }
}

// Alias mantido para compatibilidade
export const prepararModoOffline = sincronizarDadosMestres;

// =============================================
// AUTO-SYNC: sincroniza automaticamente quando online
// =============================================

export function deveAutoSincronizarMontagens(): boolean {
  if (!navigator.onLine) return false;
  if (syncEmAndamento) return false;
  if (!getConferenteToken()) return false;
  if (Date.now() - ultimoSyncMontagens < SYNC_INTERVAL_MONTAGENS) return false;
  return true;
}

export function deveAutoSincronizarConferencias(): boolean {
  if (!navigator.onLine) return false;
  if (syncEmAndamento) return false;
  if (!getConferenteToken()) return false;
  if (Date.now() - ultimoSyncConferencias < SYNC_INTERVAL_CONFERENCIAS) return false;
  return true;
}

export async function autoSincronizarMontagens(): Promise<void> {
  if (!deveAutoSincronizarMontagens()) return;
  try {
    await sincronizarMontagensCargas();
  } catch {
    // Silenciar - auto-sync nao deve bloquear o uso
  }
}

export async function autoSincronizarConferencias(): Promise<void> {
  if (!deveAutoSincronizarConferencias()) return;
  try {
    await sincronizarConferencias();
  } catch {
    // Silenciar
  }
}

// Atualiza metadata parcial sem refazer sync completo
async function atualizarMetaParcial(): Promise<void> {
  try {
    const [clientes, filiais, areas, operacoes, conferencias, montagens] = await Promise.all([
      carregarDados('clientes'),
      carregarDados('filiais'),
      carregarDados('areas'),
      carregarDados('operacoes'),
      carregarDados('conferencias'),
      carregarDados('montagens'),
    ]);
    const meta: OfflineMeta = {
      preparado_em: new Date().toISOString(),
      clientes_count: (clientes || []).length,
      filiais_count: (filiais || []).length,
      areas_count: (areas || []).length,
      operacoes_count: (operacoes || []).length,
      conferencias_count: (conferencias || []).length,
      montagens_count: (montagens || []).length,
    };
    await salvarMeta(meta);
  } catch {
    // Silenciar
  }
}

// Mantido para compatibilidade - faz sync completo
export async function autoSincronizar(): Promise<OfflineMeta | null> {
  if (!navigator.onLine || syncEmAndamento || !getConferenteToken()) return null;
  try {
    return await sincronizarDadosMestres();
  } catch {
    return null;
  }
}

// =============================================
// STATUS E VERIFICACAO
// =============================================

export async function getStatusOffline(): Promise<OfflineStats> {
  try {
    const meta = await carregarMeta();
    return { pronto: !!meta, meta };
  } catch {
    return { pronto: false, meta: null };
  }
}

// =============================================
// LIMPEZA SELETIVA
// So limpa conferencias que ja foram sincronizadas com o servidor.
// Dados mestres (clientes, areas, filiais, operacoes) NUNCA sao limpos.
// Cache NUNCA e limpo por logout ou por estar offline.
// =============================================

export async function limparConferenciaFinalizada(conferenciaId: number): Promise<void> {
  // So remove se estiver online (confirma que foi sincronizada)
  if (!navigator.onLine) return;

  const conferencias = await carregarDados('conferencias');
  if (!conferencias) return;
  const filtradas = conferencias.filter((c: any) => c.id !== conferenciaId);
  await salvarDados('conferencias', filtradas);

  const meta = await carregarMeta();
  if (meta) {
    meta.conferencias_count = filtradas.length;
    await salvarMeta(meta);
  }
}

// Limpa apenas conferencias concluidas/canceladas que ja estao no servidor
// (tem ID positivo = ja sincronizadas). Chamada somente quando online.
export async function limparConferenciasSincronizadas(): Promise<number> {
  if (!navigator.onLine) return 0;

  const conferencias = await carregarDados('conferencias');
  if (!conferencias) return 0;

  // Manter: em aberto OU com ID temporario (negativo = nao sincronizado)
  const manter = conferencias.filter((c: any) =>
    (c.status !== 'concluida' && c.status !== 'cancelada') ||
    (c.id && c.id < 0)
  );
  const removidas = conferencias.length - manter.length;

  if (removidas > 0) {
    await salvarDados('conferencias', manter);
    const meta = await carregarMeta();
    if (meta) {
      meta.conferencias_count = manter.length;
      await salvarMeta(meta);
    }
  }

  return removidas;
}

// Limpar TODOS os dados offline (uso interno apenas - NAO chamar no logout)
export async function limparDadosOffline(): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.clear();
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error || new Error('Transacao abortada')); };
  });
}

// =============================================
// LOOKUPS OFFLINE (para uso pelos componentes)
// =============================================

export async function buscarClienteOfflinePorCnpj(cnpj: string): Promise<any | null> {
  const clientes = await carregarDados('clientes');
  if (!clientes) return null;
  const cnpjLimpo = cnpj.replace(/\D/g, '');
  return clientes.find((c: any) => c.cnpj?.replace(/\D/g, '') === cnpjLimpo) || null;
}

export async function buscarClienteOfflinePorId(id: number): Promise<any | null> {
  const clientes = await carregarDados('clientes');
  if (!clientes) return null;
  return clientes.find((c: any) => c.id === id) || null;
}

export async function listarFiliaisOffline(): Promise<any[]> {
  return (await carregarDados('filiais')) || [];
}

export async function listarAreasOffline(): Promise<any[]> {
  return (await carregarDados('areas')) || [];
}

export async function listarOperacoesOffline(): Promise<any[]> {
  return (await carregarDados('operacoes')) || [];
}

export async function listarConferenciasOffline(): Promise<any[]> {
  return (await carregarDados('conferencias')) || [];
}

export async function listarMontagensOffline(): Promise<any[]> {
  return (await carregarDados('montagens')) || [];
}

export async function buscarConferenciaOffline(id: number): Promise<any | null> {
  const conferencias = await carregarDados('conferencias');
  if (!conferencias) return null;
  return conferencias.find((c: any) => c.id === id) || null;
}

export async function identificarAreaOffline(codigo: string, clienteId: number): Promise<any | null> {
  const areas = await carregarDados<any>('areas');
  if (!areas) return null;

  const codigoTrimmed = String(codigo).trim();

  // 1. Tentar pelo código secundário do cliente específico
  if (clienteId) {
    for (const area of areas) {
      if (area.codigos_cliente && Array.isArray(area.codigos_cliente)) {
        const codigoCliente = area.codigos_cliente.find((cc: any) =>
          cc.codigo === codigoTrimmed && cc.cliente_id === clienteId
        );
        if (codigoCliente) {
          return { encontrado: true, tipo: 'secundario', area };
        }
      }
    }
  }

  // 2. Fallback: tentar pelo código secundário de qualquer cliente
  for (const area of areas) {
    if (area.codigos_cliente && Array.isArray(area.codigos_cliente)) {
      const codigoQualquer = area.codigos_cliente.find((cc: any) => cc.codigo === codigoTrimmed);
      if (codigoQualquer) {
        return { encontrado: true, tipo: 'secundario', area };
      }
    }
  }

  // 3. Tentar pelo código principal da área
  const areaPrincipal = areas.find((a: any) => a.codigo === codigoTrimmed);
  if (areaPrincipal) {
    return { encontrado: true, tipo: 'principal', area: areaPrincipal };
  }

  return null;
}

export async function validarPaleteOffline(clienteId: number, numeroPalete: number, areaEntregaId?: number): Promise<any | null> {
  const operacoes = await carregarDados<any>('operacoes');
  if (!operacoes) return null;

  for (const op of operacoes) {
    if (!op.paletes) continue;
    if (op.cliente_id && op.cliente_id !== clienteId) continue;
    for (const pal of op.paletes) {
      if (pal.numero_palete === numeroPalete) {
        if (areaEntregaId && pal.area_entrega_id !== areaEntregaId) continue;
        return {
          success: true,
          palete: {
            id: pal.id,
            numero_palete: pal.numero_palete,
            operacao_id: op.id,
            numero_operacao: op.numero_operacao,
            area_entrega_id: pal.area_entrega_id,
            area_nome: pal.area_nome || '',
            area_codigo: pal.area_codigo || '',
            area_regiao: pal.area_regiao || '',
            doca: pal.doca,
            tipo_palete: pal.tipo_palete || 'normal',
            data_agendamento: pal.data_agendamento || null,
          },
        };
      }
    }
  }

  return null;
}

export async function buscarDadosPaleteCarregamentoOffline(clienteId: number | null, numeroPalete: number): Promise<any | null> {
  const operacoes = await carregarDados<any>('operacoes');
  if (!operacoes) return null;

  for (const op of operacoes) {
    if (!op.paletes) continue;
    if (clienteId && op.cliente_id && op.cliente_id !== clienteId) continue;
    for (const pal of op.paletes) {
      if (pal.numero_palete === numeroPalete) {
        // Buscar dados do cliente da operação
        let clienteInfo = null;
        if (op.cliente_id) {
          const clientes = await carregarDados<any>('clientes');
          if (clientes) {
            const cliente = clientes.find((c: any) => c.id === op.cliente_id);
            if (cliente) {
              clienteInfo = {
                id: cliente.id,
                razao_social: cliente.razao_social,
                cnpj: cliente.cnpj,
              };
            }
          }
        }

        return {
          success: true,
          palete: {
            id: pal.id,
            numero_palete: pal.numero_palete,
            doca: pal.doca || null,
            area_entrega_id: pal.area_entrega_id,
            area_nome: pal.area_nome || '',
            area_codigo: pal.area_codigo || '',
            area_regiao: pal.area_regiao || '',
            operacao_id: op.id,
            numero_operacao: op.numero_operacao,
            tipo_palete: pal.tipo_palete || 'normal',
            data_agendamento: pal.data_agendamento || null,
          },
          cliente: clienteInfo,
          leituras: [],
          total_volumes: 0,
          total_esperado: 0,
          volumes_faltantes: 0,
          tem_divergencia: false,
          disponivel: true,
          tem_ponto_corte: false,
          pedidos_com_corte: [],
        };
      }
    }
  }

  return null;
}

export async function buscarDadosPaleteDescarregamentoOffline(numeroPalete: number): Promise<any | null> {
  const operacoes = await carregarDados<any>('operacoes');
  if (!operacoes) return null;

  for (const op of operacoes) {
    if (!op.paletes) continue;
    for (const pal of op.paletes) {
      if (pal.numero_palete === numeroPalete) {
        const clientes = await carregarDados<any>('clientes');
        let clienteInfo = null;
        let etiquetaConfigs: any[] = [];
        let etiquetaModelos: any[] = [];

        if (clientes && pal.cliente_id) {
          const cliente = clientes.find((c: any) => c.id === pal.cliente_id);
          if (cliente) {
            clienteInfo = { id: cliente.id, razao_social: cliente.razao_social, cnpj: cliente.cnpj };
            etiquetaConfigs = cliente.etiqueta_configs || [];
            etiquetaModelos = cliente.etiqueta_modelos || [];
          }
        }

        if (!clienteInfo && clientes && op.cliente_id) {
          const cliente = clientes.find((c: any) => c.id === op.cliente_id);
          if (cliente) {
            clienteInfo = { id: cliente.id, razao_social: cliente.razao_social, cnpj: cliente.cnpj };
            etiquetaConfigs = cliente.etiqueta_configs || [];
            etiquetaModelos = cliente.etiqueta_modelos || [];
          }
        }

        return {
          success: true,
          palete: {
            id: pal.id,
            numero_palete: pal.numero_palete,
            doca: pal.doca || null,
            area_entrega_id: pal.area_entrega_id,
            area_nome: pal.area_nome || '',
            area_codigo: pal.area_codigo || '',
            area_regiao: pal.area_regiao || '',
            operacao_id: op.id,
            numero_operacao: op.numero_operacao,
            tipo_palete: pal.tipo_palete || 'normal',
            data_agendamento: pal.data_agendamento || null,
          },
          cliente: clienteInfo,
          etiqueta_configs: etiquetaConfigs,
          etiqueta_modelos: etiquetaModelos,
          leituras: [],
          total_volumes: 0,
          tem_ponto_corte: false,
          pedidos_com_corte: [],
        };
      }
    }
  }

  return null;
}

export async function listarPaletesCarregamentoOffline(clienteId?: number | null): Promise<any | null> {
  const operacoes = await carregarDados<any>('operacoes');
  if (!operacoes) return null;

  const paletes: any[] = [];
  for (const op of operacoes) {
    if (!op.paletes) continue;
    if (clienteId && op.cliente_id && op.cliente_id !== clienteId) continue;
    if (op.status === 'cancelada') continue;
    for (const pal of op.paletes) {
      paletes.push({
        id: pal.id,
        numero_palete: pal.numero_palete,
        doca: pal.doca || null,
        area_nome: pal.area_nome || '',
        area_codigo: pal.area_codigo || '',
        area_regiao: pal.area_regiao || '',
        operacao_id: op.id,
        numero_operacao: op.numero_operacao,
        tipo_palete: pal.tipo_palete || 'normal',
        data_agendamento: pal.data_agendamento || null,
        total_volumes: 0,
        total_esperado: 0,
        volumes_faltantes: 0,
        tem_divergencia: false,
        disponivel: true,
        tem_ponto_corte: false,
        pedidos_com_corte: [],
      });
    }
  }

  if (paletes.length === 0) return null;

  paletes.sort((a: any, b: any) => (a.area_nome || '').localeCompare(b.area_nome || ''));

  return {
    success: true,
    paletes,
    resumo: {
      total: paletes.length,
      disponiveis: paletes.length,
      divergentes: 0,
    },
  };
}
